// helpers
import { select } from 'd3-selection';
import { format } from 'd3-format';
import { wrapLongLabels } from './utils/helpers';
import 'd3-transition';

// styles
import './styles/graphic.scss';

const formatNumber = format(',');
const formatTick = (d) => {
  if (d >= 1000) return format('.2~s')(d).replace('k', 'K');
  return d;
};

const TRANSITION_DURATION = 750;

let svg;
let g;
let xAxisGroup;
let yAxisGroup;
let dataGroup;
let currentMargins;

export function initChart(props, container, isMobile) {
  const {
    colorScale,
    data,
    height,
    initialWidth,
    initialHeight,
    keys,
    margins,
    xAxis,
    x,
    yAxis,
    y0,
    y1,
  } = props;

  currentMargins = margins;
  const xTicks = isMobile ? 3 : 5;
  const labelWrapWidth = isMobile ? 70 : margins.left;

  const legend = container.append('ul')
    .attr('class', 'key')
    .selectAll('li')
    .data(keys)
    .enter()
    .append('li')
    .attr('class', 'key-item');

  legend.append('span')
    .attr('class', 'key-block')
    .style('background-color', d => colorScale(d));

  legend.append('label')
    .text(d => d);

  svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  xAxisGroup = g.append('g')
    .attr('class', 'x grid')
    .attr('role', 'presentation')
    .attr('transform', `translate(${[0, height]})`);

  xAxisGroup.call(xAxis(x)
    .ticks(xTicks)
    .tickSize(-height)
    .tickFormat(formatTick));

  xAxisGroup.selectAll('text')
    .attr('y', 6);

  yAxisGroup = g.append('g')
    .attr('class', 'y grid')
    .attr('role', 'presentation');

  yAxisGroup.call(yAxis(y0));

  yAxisGroup.selectAll('text')
    .attr('transform', 'translate(-5,-8)')
    .call(wrapLongLabels, labelWrapWidth);

  dataGroup = g.append('g')
    .attr('class', 'data');

  renderBars(props, isMobile, false);
}

export function updateChart(props, isMobile) {
  const {
    height,
    initialWidth,
    initialHeight,
    margins,
    xAxis,
    x,
    yAxis,
    y0,
  } = props;

  currentMargins = margins;
  const xTicks = isMobile ? 3 : 5;
  const labelWrapWidth = isMobile ? 55 : margins.left;

  svg
    .transition()
    .duration(TRANSITION_DURATION)
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  g
    .transition()
    .duration(TRANSITION_DURATION)
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  xAxisGroup
    .transition()
    .duration(TRANSITION_DURATION)
    .attr('transform', `translate(${[0, height]})`)
    .call(xAxis(x)
      .ticks(xTicks)
      .tickSize(-height)
      .tickFormat(formatTick));

  xAxisGroup.selectAll('text')
    .attr('y', 6);

  yAxisGroup.call(yAxis(y0));

  yAxisGroup.selectAll('text')
    .attr('transform', 'translate(-5,-8)')
    .call(wrapLongLabels, labelWrapWidth);

  renderBars(props, isMobile, true);
}

function renderBars(props, isMobile, animate) {
  const {
    colorScale,
    data,
    keys,
    x,
    y0,
    y1,
  } = props;

  const [min, max] = x.domain();
  const duration = animate ? TRANSITION_DURATION : 0;

  const groups = dataGroup.selectAll('.bars')
    .data(data, d => d.crime);

  groups.exit()
    .transition()
    .duration(duration)
    .style('opacity', 0)
    .remove();

  const groupsEnter = groups.enter()
    .append('g')
    .attr('class', 'bars')
    .attr('transform', d => `translate(${[0, y0(d.crime)]})`);

  const groupsMerge = groupsEnter.merge(groups);

  groupsMerge
    .transition()
    .duration(duration)
    .attr('transform', d => `translate(${[0, y0(d.crime)]})`);

  groupsMerge.each(function (d) {
    const barGroup = select(this);
    const barData = keys.map(key => ({ key, value: d[key], crime: d.crime }));

    const bars = barGroup.selectAll('.bar')
      .data(barData, bd => bd.key);

    bars.exit()
      .transition()
      .duration(duration)
      .style('opacity', 0)
      .remove();

    const barsEnter = bars.enter()
      .append('g')
      .attr('class', 'bar');

    barsEnter.append('rect');
    barsEnter.append('text');

    const barsMerge = barsEnter.merge(bars);

    barsMerge
      .transition()
      .duration(duration)
      .attr('transform', bd => `translate(${[x(Math.min(0, bd.value)), y1(bd.key)]})`);

    barsMerge.select('rect')
      .transition()
      .duration(duration)
      .attr('width', bd => Math.abs(x(0) - x(bd.value)))
      .attr('height', y1.bandwidth())
      .style('fill', bd => colorScale(bd.key));

    barsMerge.select('text')
      .transition()
      .duration(duration)
      .attr('x', bd => (bd.value < 0 ? 0 : Math.abs(x(0) - x(bd.value))))
      .attr('y', y1.bandwidth() / 2)
      .attr('dx', bd => ((bd.value < 0 && Math.abs(bd.value / min) < 0.85) || (bd.value / max > 0.85) ? -5 : 5))
      .attr('dy', '.32em')
      .attr('text-anchor', bd => ((bd.value < 0 && Math.abs(bd.value / min) < 0.85) || (bd.value / max > 0.85) ? 'end' : 'start'))
      .attr('fill', bd => (bd.value / max > 0.85 ? '#ffffff' : '#262626'))
      .text(bd => formatNumber(bd.value));
  });
}
